import React, { useEffect, useState, useRef } from 'react';
import { convertToVideoEmbedUrl, detectVideoService, getServiceDisplayName } from '../../utils/videoServiceDetector';
import YouTubePlayer from './VideoPlayer/YouTubePlayer';
import useAutoPlaySettings from '../../hooks/useAutoPlaySettings';

function VideoModal({ video, onClose, onNext, onPrevious, hasNext, hasPrevious, enableYouTubeAutoPlay = false }) {
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const { isAutoPlayEnabled, toggleAutoPlay } = useAutoPlaySettings();
  const isFirstVideoRef = useRef(true);
  const videoService = detectVideoService(video.url);
  const shouldTruncateDescription = video.description && video.description.length > 300;
  const displayDescription = shouldTruncateDescription && !isDescriptionExpanded
    ? video.description.substring(0, 300) + '...'
    : video.description;

  // Handle video end for auto-play
  const handleVideoEnd = () => {
    if (!enableYouTubeAutoPlay) return;

    if (isAutoPlayEnabled && hasNext) {
      onNext();
    }
  };

  // Track when video changes
  useEffect(() => {
    isFirstVideoRef.current = false;
  }, [video]);

  useEffect(() => {
    // Handle keyboard navigation
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowRight' && hasNext) {
        onNext();
      } else if (e.key === 'ArrowLeft' && hasPrevious) {
        onPrevious();
      }
    };

    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose, onNext, onPrevious, hasNext, hasPrevious]);

  if (!video) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90"
      onClick={onClose}
    >
      {/* Modal Content */}
      <div
        className="relative w-full max-w-6xl mx-4 bg-black"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors p-2"
          aria-label="Close"
        >
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Video Player */}
        <div className="aspect-video bg-black">
          {enableYouTubeAutoPlay && videoService === 'youtube' ? (
            <YouTubePlayer
              video={video}
              autoPlay={!isFirstVideoRef.current}
              onVideoEnd={handleVideoEnd}
            />
          ) : (
            <iframe
              width="100%"
              height="100%"
              src={convertToVideoEmbedUrl(video.url)}
              title={video.title || video.description || 'Video'}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
            ></iframe>
          )}
        </div>

        {/* Video Info */}
        <div className="bg-gray-900 text-white p-6">
          <h2 className="text-xl font-semibold mb-2">
            {video.title || video.description || 'Untitled Video'}
          </h2>
          {video.description && video.title && (
            <div className="mb-3">
              <p className="text-gray-300 text-sm leading-relaxed whitespace-pre-line">
                {displayDescription}
              </p>
              {shouldTruncateDescription && (
                <button
                  onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                  className="text-primary-400 hover:text-primary-300 text-sm font-medium mt-2 transition-colors"
                >
                  {isDescriptionExpanded ? 'Show less' : 'Show more'}
                </button>
              )}
            </div>
          )}

          {/* Auto-play Toggle - Only show for YouTube-only galleries */}
          {enableYouTubeAutoPlay && (
            <div className="mb-4 pb-4 border-b border-gray-700">
              <button
                onClick={toggleAutoPlay}
                className="flex items-center gap-3 w-full hover:bg-gray-800 p-2 rounded transition-colors"
              >
                <div className="flex items-center gap-2 flex-1">
                  <svg className="w-5 h-5 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-sm font-medium text-gray-300">Auto-play next video</span>
                </div>
                <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  isAutoPlayEnabled ? 'bg-primary-500' : 'bg-gray-600'
                }`}>
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    isAutoPlayEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </div>
              </button>
            </div>
          )}

          <a
            href={video.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-primary-400 hover:text-primary-300 text-sm font-medium transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
            Watch on {getServiceDisplayName(detectVideoService(video.url))}
          </a>
        </div>

        {/* Navigation Arrows */}
        {hasPrevious && (
          <button
            onClick={onPrevious}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-75 text-white p-3 rounded-full transition-all"
            aria-label="Previous video"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}

        {hasNext && (
          <button
            onClick={onNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-75 text-white p-3 rounded-full transition-all"
            aria-label="Next video"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

export default VideoModal;
